import { useStore } from "../stores";
import { useUserStore } from "../stores/user";
import userAxios from "./axios";
import { updateUserPoints } from "../classes/user";

/**
 * Load and initalize Facebook scripts
 *  
 * @param appId 
 * @param appApiVersion 
 * @returns void|true if appId is provided and we started Facebook scripts to load
 */
export function initializeFacebook(appId: string, appApiVersion: string|number): void|true
{
	if(!appId) return;

	const store = useStore();
	const userStore = useUserStore();

	// Load the Facebook SDK asynchronously
	(function(id: string){
		const userLanguage = useStore().currentLanguage

		if(document.getElementById(id)) return;

		// Load the right SDK with autodetected language
		var js = document.createElement('script')
		js.id = id
		js.async = true
		js.defer = true
		js.crossOrigin = "anonymuos"
		js.src = "//connect.facebook.net/" + (userLanguage || window.navigator.language).replace("-", "_") + "/sdk.js"

		const fjs = document.getElementsByTagName('script')[0]
		fjs.parentNode?.insertBefore(js, fjs)
	})('facebook-jssdk')

	// Async init FB scripts
	window.fbAsyncInit = () => {
		window.FB.init({
			appId,
			cookie: true,

			// the session
			version: appApiVersion
		})

		// we run this init in different environments so need to check if we have respective store mutation
		store.facebookLoaded = true

		if (userStore.connectionStatus.facebook == 'connected') { // Check status if loaded
			checkStatus()
		}
	}

	return true;
}

// Check status of current Facebook connection
function checkStatus () {
	// Now that we've initialized the JavaScript SDK, we call
	// FB.getLoginStatus().	This function gets the state of the
	// person visiting this page and can return one of three states to
	// the callback you provide.	They can be:
	//
	// 1. Logged into your app ('connected')
	// 2. Logged into Facebook, but not your app ('not_authorized')
	// 3. Not logged into Facebook and can't tell if they are logged into
	//		your app or not.
	//
	// These three cases are handled in the callback function.
	if (typeof window.FB == 'undefined') return; // Skip if FB is not yet ready

	window.FB.getLoginStatus(async (response: any) => {
		const store = useStore()
		const userStore = useUserStore()

		if(!response || response.status != 'connected') return;
		if(!response.authResponse) return; // Sometimes FB returns empty result. Preventing an error in this case
		
		store.facebookUserId = response.authResponse.userID
		
		// Try to connect user on server as well
		let data;
		try{
			const response2 = await userAxios.post('/api/user-side/connectFacebookUser', {
				token: response.authResponse.accessToken
			})
			data = response2.data
		} catch(e: any) {
			data = e.response.data
		}

		// Throw error except when fb is already assigned to another user
		if(data?.error_code == "OtherUserIsConnectedWithSameExternalAccount") {
			return userStore.connectionStatus.facebook = "error-duplicate-account"
		}

		// Throw error when user if connencting with another FB account
		if(data?.error_code == "UserIsConnectedWithOtherExternalAccount"){
			return userStore.connectionStatus.facebook = "error-other"
		}

		// If you have status never, then this is your first login. Let's update points.
		if (userStore.connectionStatus.facebook == 'never') {
			// Update points if this is a first time connection
			await updateUserPoints()
		}
		
		await loadFriends()

		userStore.connectionStatus.facebook = "connected"
	})
}

/**
 * Load current user friends into store
 */
export async function loadFriends()
{
	const store = useStore()
	const userStore = useUserStore()

	if (!store.settings.userFriendsEnabled || !userStore.connectionStatus?.facebook) {
		return;
	}

	// Load friends data from server
	const response = await userAxios.get('/api/user-side/getUserFriends')

	let friends = response.data
	const friendsIndex: Record<string, any> = {}

	for (let i = 0; i < friends.length; i++) {
		// Add friend to the index
		friendsIndex[friends[i].id] = friends[i];
	}
	
	// Add me to the list
	const me = {
		id: store.facebookUserId,
		me: true,
	};

	friendsIndex[me.id] = me
	// Add me to the end of friends list
	friends[friends.length] = me

	// Sort list by points
	friends.sort((a: { points: number; }, b: { points: number; }) => b.points - a.points)

	// Take only top 5 friends
	friends = friends.slice(0, 5)
	userStore.friends = friends
	userStore.friendsIndex = friendsIndex
}
